import React from 'react';
import styled from 'styled-components';
import { Github, Linkedin, Mail, Phone } from 'lucide-react';
import { siteConfig } from '../config/siteConfig';

const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: 2rem auto 2.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.md};
    flex-wrap: wrap;
  }
`;

const IconButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.cardBg};
  border: ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 200ms cubic-bezier(0.2, 0.8, 0.2, 1),
    background-color 200ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background-color: rgba(0,0,0,0.06);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(127,127,127,0.35), ${({ theme }) => theme.shadows.md};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 44px;
    height: 44px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ContactActions: React.FC = () => {
  const actions = [
    {
      href: siteConfig.socialLinks.email,
      label: 'Email via Gmail',
      icon: <Mail size={22} strokeWidth={1.75} aria-hidden="true" />,
      target: undefined,
    },
    {
      href: siteConfig.socialLinks.linkedin,
      label: 'LinkedIn profile',
      icon: <Linkedin size={22} strokeWidth={1.75} aria-hidden="true" />,
      target: '_blank' as const,
    },
    {
      href: siteConfig.socialLinks.github,
      label: 'GitHub profile',
      icon: <Github size={22} strokeWidth={1.75} aria-hidden="true" />,
      target: '_blank' as const,
    },
    {
      href: siteConfig.socialLinks.phone,
      label: 'Call phone',
      icon: <Phone size={22} strokeWidth={1.75} aria-hidden="true" />,
      target: undefined,
    },
  ];

  return (
    <ActionsRow aria-label="Contact actions">
      {actions.map((action) => (
        <IconButton
          key={action.label}
          href={action.href}
          aria-label={action.label}
          title={action.label}
          {...(action.target ? { target: action.target, rel: 'noreferrer' } : {})}
        >
          {action.icon}
          <VisuallyHidden>{action.label}</VisuallyHidden>
        </IconButton>
      ))}
    </ActionsRow>
  );
};

export default ContactActions;
